
import Hero from "@/components/Hero";
import Features from "@/components/Features";
import { Pricing } from "@/components/Pricing";
import CostComparison from "@/components/CostComparison";
import SocialProof from "@/components/SocialProof";
import DemoVideo from "@/components/DemoVideo";
import TrustSignals from "@/components/TrustSignals";
import Footer from "@/components/Footer";
import { LegalDialog } from "@/components/LegalDialogs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Gift, DollarSign, Users, TrendingUp, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";

const Index = () => {
  console.log('Index page is rendering');
  const navigate = useNavigate();

  return (
    <div className="min-h-screen">
      <Hero />
      <TrustSignals />
      <SocialProof />
      <DemoVideo />
      <Features />
      <CostComparison />
      
      <Pricing />
      
      {/* Referral Program Section */}
      <section id="referral-program" className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 bg-gradient-to-b from-green-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="flex justify-center mb-4">
              <Gift className="h-12 w-12 text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-green-800 mb-2">
              Earn Money with Our Referral Program
              <Badge className="ml-3 bg-orange-500 text-white">BETA</Badge>
            </h2>
            <p className="text-lg text-green-700 mb-2">
              Share CSO Agent with your network and earn commissions on every successful referral
            </p>
            <p className="text-orange-600 font-medium">
              Beta Program - Features and commission rates may change during development
            </p>
          </div>

          {/* Commission Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8 sm:mb-12">
            <Card className="text-center border-green-200">
              <CardContent className="p-6">
                <DollarSign className="h-8 w-8 text-green-600 mx-auto mb-3" />
                <div className="text-2xl font-bold text-green-600 mb-2">$5 per Monthly</div>
                <p className="text-sm text-green-700">Commission for each monthly subscriber</p>
              </CardContent>
            </Card>
            
            <Card className="text-center border-green-200">
              <CardContent className="p-6">
                <TrendingUp className="h-8 w-8 text-green-600 mx-auto mb-3" />
                <div className="text-2xl font-bold text-green-600 mb-2">$20 per Team</div>
                <p className="text-sm text-green-700">Higher commissions for team plans</p>
              </CardContent>
            </Card>

            <Card className="text-center border-green-200">
              <CardContent className="p-6">
                <Gift className="h-8 w-8 text-green-600 mx-auto mb-3" />
                <div className="text-2xl font-bold text-green-600 mb-2">$80 per Enterprise</div>
                <p className="text-sm text-green-700">Maximum earnings for enterprise referrals</p>
              </CardContent>
            </Card>

            <Card className="text-center border-green-200">
              <CardContent className="p-6">
                <Users className="h-8 w-8 text-green-600 mx-auto mb-3" />
                <div className="text-2xl font-bold text-green-600 mb-2">$175 per Lifetime</div>
                <p className="text-sm text-green-700">Highest commission for lifetime plans</p>
              </CardContent>
            </Card>
          </div>

          {/* How it works */}
          <div className="bg-white rounded-lg p-4 sm:p-6 lg:p-8 shadow-sm border border-green-200 mb-6 sm:mb-8">
            <h3 className="text-lg sm:text-xl font-bold text-center mb-4 sm:mb-6">How it works:</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 text-center">
              <div>
                <div className="bg-green-100 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3">
                  <span className="font-bold text-green-600">1</span>
                </div>
                <p className="text-gray-700">Sign up and get your unique referral code</p>
              </div>
              <div>
                <div className="bg-green-100 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3">
                  <span className="font-bold text-green-600">2</span>
                </div>
                <p className="text-gray-700">Share your link with colleagues and network</p>
              </div>
              <div>
                <div className="bg-green-100 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3">
                  <span className="font-bold text-green-600">3</span>
                </div>
                <p className="text-gray-700">Earn commissions when they subscribe</p>
              </div>
              <div>
                <div className="bg-green-100 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3">
                  <span className="font-bold text-green-600">4</span>
                </div>
                <p className="text-gray-700">Track your earnings in real-time</p>
              </div>
            </div>
          </div>

          {/* Payment Methods */}
          <div className="text-center mb-6 sm:mb-8">
            <h3 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6">Get Paid Your Way</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 max-w-2xl mx-auto">
              <Card className="border-blue-200">
                <CardContent className="p-6 text-center">
                  <div className="bg-blue-100 rounded-lg p-3 inline-block mb-3">
                    <DollarSign className="h-6 w-6 text-blue-600" />
                  </div>
                  <h4 className="font-semibold text-blue-600 mb-2">USDC Payments</h4>
                  <p className="text-sm text-gray-600">Receive payments in USDC on Base blockchain</p>
                </CardContent>
              </Card>
              
              <Card className="border-purple-200">
                <CardContent className="p-6 text-center">
                  <div className="bg-purple-100 rounded-lg p-3 inline-block mb-3">
                    <Sparkles className="h-6 w-6 text-purple-600" />
                  </div>
                  <h4 className="font-semibold text-purple-600 mb-2">PayPal Payments</h4>
                  <p className="text-sm text-gray-600">Traditional PayPal transfers available</p>
                </CardContent>
              </Card>
            </div>
            <p className="text-sm text-gray-500 mt-4">
              Minimum payout: $50 • 30-day holding period for all commissions
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Index;
